import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Clock, MessageCircle, ArrowLeft, Mail, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';
import { usePartner } from '../lib/partnerContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function RadarConfirmationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const partner = usePartner(searchParams.toString());
  const sessionId = searchParams.get('session_id');
  const [demandeId, setDemandeId] = useState(searchParams.get('demande_id'));
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [error, setError] = useState(null);

  // Poll payment status when page loads
  useEffect(() => {
    if (!sessionId) {
      setLoading(false);
      setError("Session invalide");
      return;
    }

    const pollStatus = async (attempts = 0) => {
      const maxAttempts = 10;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setLoading(false);
        setPaymentStatus("timeout");
        return;
      }

      try {
        const response = await axios.get(`${API}/api/radar/checkout-status/${sessionId}`);
        const data = response.data;

        if (data.payment_status === "paid") {
          setPaymentStatus("paid");
          setDemandeId(data.demande_id);
          setLoading(false);
          return;
        } else if (data.status === "expired") {
          setPaymentStatus("expired");
          setLoading(false);
          return;
        }

        // Continue polling
        setTimeout(() => pollStatus(attempts + 1), pollInterval);
      } catch (err) {
        console.error("Error checking status:", err);
        if (attempts < maxAttempts - 1) {
          setTimeout(() => pollStatus(attempts + 1), pollInterval);
        } else {
          setError("Erreur de vérification");
          setLoading(false);
        }
      }
    };

    pollStatus();
  }, [sessionId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="bg-white/10 border-white/20 max-w-lg w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <Loader2 className="w-16 h-16 animate-spin text-purple-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Vérification du paiement...</h2>
            <p className="text-white/70">Veuillez patienter quelques secondes.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || paymentStatus === "expired" || paymentStatus === "timeout") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="bg-white/10 border-white/20 max-w-lg w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <AlertCircle className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">
              {paymentStatus === "timeout" ? "Vérification en cours..." : "Session expirée"}
            </h2>
            <p className="text-white/70 mb-6">
              {paymentStatus === "timeout" 
                ? "Le paiement est peut-être en cours de traitement. Vérifiez votre email."
                : "Veuillez réessayer le paiement."}
            </p>
            <Button
              onClick={() => navigate(partner.link('/radar'))}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Retour au Radar Foncier
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <Card className="bg-white/10 border-white/20 max-w-lg w-full">
        <CardContent className="pt-8 pb-8 text-center">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
          
          <h1 className="text-2xl font-bold text-white mb-2">Paiement confirmé !</h1>
          <p className="text-white/70 mb-6">
            Votre demande d'identification du propriétaire est en cours de traitement.
          </p>
          
          <div className="bg-blue-500/20 p-4 rounded-lg border border-blue-500/30 mb-6">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Clock className="w-5 h-5 text-blue-400" />
              <span className="font-medium text-white">Délai estimé : 7 à 10 jours ouvrés</span>
            </div>
            <p className="text-sm text-white/70">
              Vous recevrez un email dès que l'identité du propriétaire sera confirmée.
            </p>
          </div>
          
          <div className="bg-white/5 p-4 rounded-lg mb-6">
            <h3 className="font-medium text-white mb-2 flex items-center justify-center gap-2">
              <Mail className="w-4 h-4" /> Vous recevrez :
            </h3>
            <ul className="text-sm text-white/70 space-y-1">
              <li>• Nom et prénom du propriétaire</li>
              <li>• Adresse du propriétaire</li>
            </ul>
          </div>
          
          <div className="bg-purple-500/20 p-4 rounded-lg border border-purple-500/30 mb-6">
            <div className="flex items-center justify-center gap-2 mb-2">
              <MessageCircle className="w-5 h-5 text-purple-400" />
              <span className="font-medium text-white">Une question ?</span>
            </div>
            <p className="text-sm text-white/70">
              Vous pouvez contacter notre expert directement via votre espace de suivi.
            </p>
          </div>
          
          <div className="space-y-3">
            <Button
              onClick={() => navigate(partner.link(`/radar/suivi?demande=${demandeId || sessionId}`))}
              className="w-full bg-purple-600 hover:bg-purple-700"
            >
              Suivre ma demande
            </Button>
            <Button
              onClick={() => navigate(partner.link('/radar'))}
              variant="outline"
              className="w-full bg-white/10 border-white/30 text-white hover:bg-white/20"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Nouvelle recherche
            </Button>
          </div>
          
          <p className="text-white/50 text-xs mt-6">
            ⚠️ Pensez à vérifier vos spams si vous ne recevez pas nos messages.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
